import { useAxiosRequest } from "api/axiosRequest"
import { BASE_URL } from "api/url_helper"
import moment from "moment"

export enum VesselPortStayActivity {
  Load = "L",
  Discharge = "D",
  Fuel = "F",
  CanalTransit = "C",
  Yard = "Y",
}

export interface VesselPortStay {
  date_from: string
  date_to: string
  port: string
  port_locode?: string
  lon: number
  lat: number
  duration: number
  yard_duration: number
  cargo: string
  activity: VesselPortStayActivity
  leg: "laden" | "ballast" | null
  avg_speed_mov: number
  max_speed: number // Do not use this prop, use avg_speed_mov
  // avg_speed: number,
  nobs: number
}

export interface VesselDetails {
  beam: number
  built: number
  cubic: number
  design: string
  designer: string
  draft: number
  dwt: number
  imo: number
  loa: number
  shipyard: string
  shipyard_country: string
  tpc: number
  vessel_name: string
  flag_state: string | null
}

export interface NearbyContributor {
  imo: number
  vessel_name: string
  sog: number
  seamargin_sog: number
}

export interface VesselLeg {
  pos_date: string
  sog: number
  laden: boolean
  zone_code?: string
  zone_name?: string
  zone_alias?: string
  zone_group?: string
  prev_port: string
  next_port: string
  margin?: number
  zone_avg_sog?: number | null
  nearby_contributors?: NearbyContributor[] | null
  zone_avg_sm_sog?: number | null
}

export interface VesselPosition {
  pos_date: string
  updated_at: string
  lon: number
  lat: number
  sog: number
  draft_ratio: number
  draft_laden: boolean
  model_laden: boolean
  master_destination: string
  master_eta: string
}

export interface NearbyShipsFocus {
  lat: number
  lon: number
  calc_cog: number
  sog: number
}

export interface NearbyShip {
  seamargin_sog?: number | null
  margin_pct?: number | null
  master_destination?: string | null
  draft_ratio?: number | null
  master_eta?: string | null
  imo: number
  vessel_name: string
  lat: number
  lon: number
  sog: number
  calc_cog: number
  distance_nm: number
}

export interface NearbyShipsResponse {
  focus: NearbyShipsFocus | null
  nearby: NearbyShip[]
}

export interface RecentSearch {
  vesselImo: number
  vesselLabel: string
}

export interface AddRecentSearch {
  vesselImo: string
  vesselLabel: string
}

export interface GetVesselSensitivityInputStruct {
  orgId: string
  vesselImo: string
  vesselDateSelected: string
}

export interface VesselSensitivityInput {
  ballastKt: number | null
  ballastMt: number | null
  ballastSd: number | null
  ladenKt: number | null
  ladenMt: number | null
  ladenSd: number | null
  hirePrice: number | null
  fuelPrice: number | null
}
export interface VesselSensitivityInputObject extends CreateVesselSensitivityInputObject{
  updatedAt: string //ISO timestamp from backend
}
export interface CreateVesselSensitivityInputObject {
  orgId: string
  vesselImo: string
  vesselDateSelected: string
  updatedByName: string
  data: VesselSensitivityInput
}

export function usePrevetAPI() {
  const request = useAxiosRequest()

  async function getVesselStays(imo: number) {
    // Get last 5 years data
    return request<any, VesselPortStay[]>({
      url: `${BASE_URL}/prevetting/vessel-stays`,
      params: { imo },
    }).then((response) => response?.data)
  }

  //get recent search
  async function getRecentSearch() {
    return request<any, RecentSearch[]>({
      url: `${BASE_URL}/prevetting/recent-search`,
      method: "GET",
    }).then((response) =>
      response?.data.map((vessel) => ({
        ...vessel,
        vesselImo: Number(vessel.vesselImo),
      }))
    )
  }

  //add recent search
  async function addRecentSearch(search: AddRecentSearch) {
    return request<any, RecentSearch>({
      url: `${BASE_URL}/prevetting/recent-search`,
      method: "POST",
      data: search,
    })
  }

  //get vessel sensitivity input
  async function getVesselSensitivityInput(
    vsi: GetVesselSensitivityInputStruct
  ) {
    return request<any, VesselSensitivityInputObject>({
      url: `${BASE_URL}/prevetting/vessel-sensitivity-input`,
      method: "GET",
      params: vsi,
    }).then((response) => response?.data ?? null)
  }

  async function addVesselSensitivityInput(vsi: CreateVesselSensitivityInputObject) {
    return request<any, CreateVesselSensitivityInputObject>({
      url: `${BASE_URL}/prevetting/vessel-sensitivity-input`,
      method: "POST",
      data: vsi,
    })
  }

  async function getVesselDetails(imo: number) {
    return request<any, VesselDetails>({
      url: `${BASE_URL}/prevetting/vessel-details`,
      params: { imo },
    }).then((response) => response?.data)
  }

  async function getVesselLegs(imo: number, end: string, start: string) {
    // Calculate the start date based on the "start" argument
    let calculatedStart: string

    if (start === "6 Months") {
      calculatedStart = moment(end).subtract(6, "months").format("YYYY-MM-DD")
    } else if (start === "1 Year") {
      calculatedStart = moment(end).subtract(1, "year").format("YYYY-MM-DD")
    } else {
      throw new Error('Invalid "start" value. Expected "6 Months" or "1 Year".')
    }

    return request<any, VesselLeg[]>({
      // url: `${BASE_URL}/prevetting/vessel-legs`,
      url: `${BASE_URL}/prevetting/vessel-legs-v2`,
      params: { imo, start: calculatedStart, end },
    }).then((response) => response?.data)
  }

  // TODO: Use this to replace useFetchVesselPositions
  // This has been tried before but it fucked with the centering of latest marker
  async function getVesselPositions({
    imo,
    months,
  }: {
    imo: number | string
    months: number
  }) {
    return request<any, VesselPosition[]>({
      url: `${BASE_URL}/prevetting/vessel-positions`,
      params: { imo, months },
    }).then((response) => response?.data)
  }

  return {
    getVesselDetails,
    getVesselLegs,
    getVesselPositions,
    getVesselStays,
    getRecentSearch,
    addRecentSearch,
    getVesselSensitivityInput,
    addVesselSensitivityInput,
  }
}
